import classNames from 'classnames';
import { Forbidden } from '@vyaguta/vyaguta-ui';
import React, { useContext } from 'react';
import forbiddenRibby from 'assets/images/forbidden-ribby.svg';
import { Route, type RouteComponentProps, type RouteProps, matchPath } from 'react-router-dom';

import { UserContext } from 'components/App';
import Loading from 'components/common/loading/Loading';

import { HOME } from 'constants/routes';

import history from 'utils/history';

import { useMyRoleQuery } from 'hooks/query/useMyRole';
import { useUserInfoQuery, useUserPermissions } from 'hooks/query/useUserInfo';

type PermissionMap = Record<string, string | number | boolean | undefined>;

type AuthorizedRouteComponent = React.ComponentType<
  RouteComponentProps<Record<string, string | undefined>> & Record<string, unknown>
>;

type AuthorizedRouteRender = (props: RouteComponentProps<Record<string, string | undefined>>) => React.ReactNode;

interface AuthorizedRouteProps extends Omit<RouteProps, 'component' | 'render'> {
  component?: AuthorizedRouteComponent;
  render?: AuthorizedRouteRender;
  permission?: string[];
  allowSelf?: boolean;
  customClassName?: string;
  forbiddenRoles?: string[];
}

function getViewAccess(
  permissions: PermissionMap | undefined,
  requiredPermissions: string[] | undefined,
  forbiddenRoles: string[] | undefined,
  isSubContractor: boolean,
  allowSelf: boolean | undefined,
  loggedInUserId?: string | number,
  routeParamId?: string | number
): boolean {
  if (forbiddenRoles?.length && !requiredPermissions?.length) {
    if (isSubContractor) {
      return false;
    }
    return true;
  }

  // Allow self-access if allowed and IDs match
  if (
    allowSelf &&
    loggedInUserId !== undefined &&
    routeParamId !== undefined &&
    String(loggedInUserId) === String(routeParamId)
  ) {
    return true;
  }

  const canView = requiredPermissions?.some((permission) => Boolean(permissions?.[permission]));
  return Boolean(canView);
}

const AuthorizedRoute: React.FC<AuthorizedRouteProps> = ({
  component: Component,
  render,
  permission = [],
  allowSelf,
  customClassName,
  forbiddenRoles,
  ...rest
}) => {
  const user = useContext(UserContext);
  const permissions = useUserPermissions();
  const { isLoading: isPermissionLoading } = useUserInfoQuery();
  const userId = typeof user === 'string' ? undefined : Number(user.id);
  const { data: userRoles = [], isLoading: isUserRoleLoading } = useMyRoleQuery(userId);

  const isSubContractor = forbiddenRoles?.some((item) => userRoles.includes(item));
  const isLoading = isPermissionLoading || isUserRoleLoading;

  if (isLoading) {
    return (
      <div className="container flash-card">
        <div className="full-scope-card loading__container">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <Route
      {...rest}
      render={(props) => {
        const match = matchPath<{ id?: string }>(history.location.pathname, {
          path: rest.path as string,
        });
        const routeParamId = match?.params?.id;
        const loggedInUserId = typeof user !== 'string' ? user.id : undefined;
        const canView = getViewAccess(
          permissions,
          permission,
          forbiddenRoles,
          Boolean(isSubContractor),
          allowSelf,
          loggedInUserId,
          routeParamId
        );
        if (!canView) {
          return (
            <Forbidden
              redirectHandler={() => history.push(HOME)}
              className={classNames('flash-card', { [customClassName ?? '']: Boolean(customClassName) })}
              title="Access Denied"
              message="You don't have permission to access the requested page."
              btnMessage="Go to dashboard"
              image={forbiddenRibby}
            />
          );
        }

        if (render) {
          return render(props);
        }

        if (!Component) {
          return null;
        }

        return <Component {...rest} {...props} />;
      }}
    />
  );
};

export default AuthorizedRoute;
